import { User, GraduationCap, Target, Briefcase, BookOpen } from 'lucide-react';
import data from '../../../shared/talentTypes.json';

export const TALENT_TYPES = data.TALENT_TYPES;
export const TALENT_ONLY_TYPES = data.TALENT_ONLY_TYPES;
export const STATUS_TYPES = data.STATUS_TYPES;
export const DEPARTURE_KINDS = data.DEPARTURE_KINDS;
export const TALENT_TYPE_LABELS = data.TALENT_TYPE_LABELS;
export const TALENT_TYPE_HEX_COLORS = data.TALENT_TYPE_HEX_COLORS;

export const ALL_TALENT_TYPES = Object.values(TALENT_TYPES);

// Цвета с поддержкой тёмной темы (не хранятся в JSON — содержат Tailwind dark: классы)
export const TALENT_TYPE_COLORS = {
  candidate: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  trainee: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  employee: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  presale: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  job_application: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
};

export const isValidTalentType = (type) => TALENT_ONLY_TYPES.includes(type);
export const isValidStatusType = (type) => STATUS_TYPES.includes(type);

export const getTalentTypeLabel = (type) => TALENT_TYPE_LABELS[type] || type;
export const getTalentTypeColor = (type) => TALENT_TYPE_COLORS[type] || 'bg-gray-100 text-gray-800';
export const getTalentTypeHexColor = (type) => TALENT_TYPE_HEX_COLORS[type] || '#6B7280';

export const getTalentTypeOptions = () =>
  TALENT_ONLY_TYPES.map(type => ({ value: type, label: getTalentTypeLabel(type) }));

export const getStatusTypeOptions = () =>
  STATUS_TYPES.map(type => ({ value: type, label: getTalentTypeLabel(type) }));

// Типы для фильтра статусов: тип таланта (статус ищется по его стадиям) либо
// сущность без стадий (заявка, пост, шаблон)
export const getStatusFilterTypeOptions = () =>
  [...TALENT_ONLY_TYPES, ...STATUS_TYPES.filter(type => type !== 'talent')]
    .map(type => ({ value: type, label: getTalentTypeLabel(type) }));

/**
 * К каким типам относится статус. У статуса таланта своего типа нет — его
 * задают стадии, к которым статус привязан (status.talent_types с бэкенда).
 * Статус таланта без стадий возвращает ['talent'], прочие — свою сущность.
 */
export const getStatusTypes = (status) => {
  if (!status) return [];
  if (Array.isArray(status.talent_types) && status.talent_types.length > 0) return status.talent_types;
  return status.talent_type ? [status.talent_type] : [];
};

/** Есть ли у двух статусов общая стадия (иначе переход между ними невозможен) */
export const statusesShareStage = (a, b) => {
  const stageIds = new Set((a?.stages || []).map(stage => stage.id));
  return (b?.stages || []).some(stage => stageIds.has(stage.id));
};

// Признак «статус ухода» (talent_statuses.departure_kind): по нему, а не по
// имени статуса, работает авто-увольнение по важной дате
export const DEPARTURE_KIND_LABELS = { fired: 'Fired (dismissed)', left: 'Left (resigned)' };
export const getDepartureKindOptions = () => [
  { value: '', label: 'Not a departure status' },
  ...DEPARTURE_KINDS.map(kind => ({ value: kind, label: DEPARTURE_KIND_LABELS[kind] || kind }))
];

export const getTalentTypeIcon = (type) => {
  const icons = {
    candidate: GraduationCap,
    trainee: BookOpen,
    employee: User,
    presale: Target,
    job_application: Briefcase
  };
  return icons[type] || User;
};

export const getAvailableTypesForTransition = (currentType) =>
  TALENT_ONLY_TYPES.filter(type => type !== currentType);

// Хелперов смены типа здесь больше нет: они дергали общий updateTalent с одним
// talent_type, а тот статус на новый тип не переносит и теперь такую пару
// отклоняет. Тип меняется только через talents.changeTalentType (PATCH /:id/type).

export default {
  TALENT_TYPES,
  ALL_TALENT_TYPES,
  TALENT_ONLY_TYPES,
  STATUS_TYPES,
  TALENT_TYPE_LABELS,
  TALENT_TYPE_COLORS,
  TALENT_TYPE_HEX_COLORS,
  isValidTalentType,
  isValidStatusType,
  getTalentTypeLabel,
  getTalentTypeColor,
  getTalentTypeHexColor,
  getTalentTypeOptions,
  getStatusTypeOptions,
  getTalentTypeIcon,
  getAvailableTypesForTransition
};
